// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/react';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';

import { useUser } from '@store';
import { useUpdateAppointmentMutation } from '../../appointments/queries/updateAppointment.mutation.generated';
import { GetServiceStopsMissingRoQuery } from '../queries/getServiceStopsMissingRo.query.generated';
import { Appointments_Set_Input } from '@gql/base-types';

import { formatPhone } from '@services/formatService';

import { css } from '@emotion/css';
import { Theme, useTheme, Button, InputAdornment, LinearProgress, TextField, Typography } from '@mui/material';
import { BiClipboard, BiEdit } from 'react-icons/bi';

import { Modal, ModalHeader, ModalContent, ModalFooter } from '@components/ModalComponents';
import DefaultFallback from '@components/Fallbacks/DefaultFallback';
import Spacer from '@components/Utils/Spacer';

import Table from '@components/TableComponents/Table';
import Column from '@components/TableComponents/Table';

// TYPES ---------------------------------------------------------------- //

export type MissingRoStop = GetServiceStopsMissingRoQuery['mobility_stops'][number];

interface MissingRoServiceStopsSectionProps {
  stops: MissingRoStop[];
  search: string;
  refetch: () => Promise<void>;
}

// HELPERS ---------------------------------------------------------------- //

const formatTime = (time?: string | null) => {
  if (time && typeof time === `string`) {
    let newTime = dayjs(time).format(`M/D/YYYY h:mm A z`);
    newTime = newTime.replace(/\s/g, '\xa0');
    return newTime;
  }
  return null;
};

/** Prettify a snake_case stop status (e.g. pending_confirmation -> Pending Confirmation) */
const formatStopStatus = (status?: string | null) => {
  if (!status) return '-';
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getStopCustomerName = (stop: MissingRoStop) =>
  stop?.service_stop_detail?.[0]?.customer_name || stop?.service_orders?.[0]?.appointment?.consumer_name || '';

const getStopCustomerPhone = (stop: MissingRoStop) =>
  stop?.service_stop_detail?.[0]?.customer_phone || stop?.service_orders?.[0]?.appointment?.consumer_phone || '';

const getStopVehicle = (stop: MissingRoStop) => {
  const orders = stop?.service_orders || [];
  if (orders.length > 1) return `${orders.length} vehicles`;
  const order = orders[0];
  if (!order) return '';
  return [order?.vehicle_year, order?.vehicle_make, order?.vehicle_model, order?.vehicle_color]
    .filter(Boolean)
    .join(' ');
};

const getStopVin = (stop: MissingRoStop) => {
  const orders = stop?.service_orders || [];
  if (orders.length > 1) return `${orders.length} VINs`;
  return orders[0]?.vehicle_vin || '';
};

const getStopScheduledTime = (stop: MissingRoStop) =>
  stop?.run?.departure_time || stop?.service_orders?.[0]?.appointment?.appointment_time || stop?.run?.created_at;

/** Split on "USA" or "United States" with regex to save some space */
const splitAddress = (address?: string | null) => address?.split(/, USA|, United States/)?.[0] || '';

/** Unique linked appointment IDs on a stop (a fleet stop may have several) */
const getStopAppointmentIds = (stop?: MissingRoStop | null) => {
  const ids = (stop?.service_orders || []).map(order => order?.appointment?.id).filter(Boolean) as number[];
  return [...new Set(ids)];
};

// ASSIGN RO MODAL ---------------------------------------------------------------- //

interface AssignStopRoModalProps {
  open: boolean;
  stop?: MissingRoStop | null;
  onClose: (output?: { roNumber: string }) => void;
}

const AssignStopRoModal = ({ open, stop, onClose }: AssignStopRoModalProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const user = useUser();

  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [roNumber, setRoNumber] = React.useState<string>('');
  const [isValid, setIsValid] = React.useState<boolean>(true);

  // A typed RO number or a running save holds an app update while the modal is open.
  useReloadGuard(open && (roNumber.trim() !== '' || isSaving), 'RO number');

  const [updateAppointment] = useUpdateAppointmentMutation();

  const appointmentIds = getStopAppointmentIds(stop);

  const handleSubmit = async () => {
    const trimmed = roNumber.trim();
    if (!trimmed) {
      setIsValid(false);
      toast.warning(`Please enter the RO #.`, { toastId: 'ro-warning' });
      return;
    }

    setIsSaving(true);

    try {
      await Promise.all(
        appointmentIds.map(id =>
          updateAppointment({
            variables: {
              id,
              changes: {
                ro_number: trimmed,
                updatedat: new Date().toISOString(),
                updatedby: user?.profile?.email || 'unknown',
              } as Appointments_Set_Input,
            },
          })
        )
      );

      toast.success(`Successfully updated RO # for Stop #${stop?.id}.`, { toastId: 'update-ro-success' });
      onClose({ roNumber: trimmed });
    } catch (err) {
      Sentry.captureException(err);
      console.error(`Failed to update RO # for Stop #${stop?.id}:`, err);
      toast.error(`Failed to update RO # for Stop #${stop?.id}!`, { toastId: 'update-ro-error' });
    }

    setIsSaving(false);
  };

  return (
    <Modal open={open}>
      {open && stop ? (
        <>
          <ModalHeader handleClose={() => onClose()}>Assign RO # (Run #{stop?.run?.id})</ModalHeader>

          <ModalContent subtitle={`Enter the RO # for this mobile service stop.`}>
            <div className={cls.infoBox}>
              {getStopCustomerName(stop) ? (
                <>
                  <Typography variant='body2' sx={{ color: theme?.palette?.text?.secondary }}>
                    Customer
                  </Typography>

                  <Typography variant='h6' sx={{ fontWeight: 700 }}>
                    {getStopCustomerName(stop)}
                  </Typography>
                </>
              ) : null}

              {getStopScheduledTime(stop) ? (
                <>
                  <Spacer />

                  <Typography variant='body2' sx={{ color: theme?.palette?.text?.secondary }}>
                    Scheduled
                  </Typography>

                  <Typography variant='body1' sx={{ fontWeight: 600 }}>
                    {dayjs(getStopScheduledTime(stop)).format('MMMM D, YYYY [at] h:mm A')}
                  </Typography>
                </>
              ) : null}
            </div>

            <Spacer />

            <div className={cls.infoBox}>
              <Typography variant='body2' sx={{ color: theme?.palette?.text?.secondary }}>
                Vehicle Information
              </Typography>

              <Typography variant='h6' sx={{ fontWeight: 700 }}>
                {getStopVehicle(stop) || '-'}
              </Typography>

              {getStopVin(stop) && <Typography variant='body2'>VIN: {getStopVin(stop)}</Typography>}
            </div>

            <Spacer size={3} />

            <TextField
              fullWidth
              label='RO #'
              value={roNumber}
              onChange={e => setRoNumber(e.target.value)}
              error={!isValid}
              helperText={isValid ? '' : `Please enter the RO #!`}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <BiClipboard size={20} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </ModalContent>

          {isSaving ? <LinearProgress /> : null}

          <ModalFooter>
            <Button variant='text' color='utility' onClick={() => onClose()}>
              Cancel
            </Button>
            <Button disabled={isSaving} color='primary' onClick={handleSubmit}>
              Submit
            </Button>
          </ModalFooter>
        </>
      ) : null}
    </Modal>
  );
};

// COMPONENT ---------------------------------------------------------------- //

const MissingRoServiceStopsSection = ({ stops, search, refetch }: MissingRoServiceStopsSectionProps) => {
  const [assignModal, setAssignModal] = React.useState<{ open: boolean; stop: MissingRoStop | null }>({
    open: false,
    stop: null,
  });

  const handleAssignModalClose = (output?: { roNumber: string }) => {
    setAssignModal({ open: false, stop: null });
    if (output) refetch();
  };

  // Apply the report's search term client-side (the moves query filters server-side)
  const filteredStops = React.useMemo(() => {
    const term = search?.trim()?.toLowerCase();
    if (!term) return stops;

    return stops.filter(stop => {
      const haystack = [
        `${stop?.id}`,
        `${stop?.run?.id}`,
        getStopCustomerName(stop),
        getStopCustomerPhone(stop),
        getStopVehicle(stop),
        stop?.address,
        ...(stop?.service_orders || []).map(order => order?.vehicle_vin),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [stops, search]);

  return (
    <>
      {/* Keyed by stop + open state so the modal's form state resets on each open */}
      <AssignStopRoModal
        key={`${assignModal.stop?.id || 0}-${assignModal.open}`}
        open={assignModal.open}
        stop={assignModal.stop}
        onClose={handleAssignModalClose}
      />

      {filteredStops.length > 0 ? (
        <Table
          data={filteredStops}
          defaultOrder='desc'
          defaultOrderBy='scheduled'
          tableAriaLabel='mobile service stops'
          rowHref={(data: object) => `/mobile-service/runs/${(data as MissingRoStop)?.run?.id}`}
        >
          <Column
            name='actions'
            value={undefined}
            renderer={(stop: MissingRoStop) => (
              <Button
                size='small'
                startIcon={<BiEdit size={16} />}
                color='primary'
                disabled={getStopAppointmentIds(stop).length === 0}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setAssignModal({ open: true, stop });
                }}
              >
                Assign
              </Button>
            )}
            style={{ whiteSpace: 'nowrap' }}
          />

          <Column
            name='run'
            value={(stop: MissingRoStop) => stop?.run?.id}
            renderer={(stop: MissingRoStop) => (
              <Typography variant='body2' sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                Run&nbsp;#{stop?.run?.id}&nbsp;&middot;&nbsp;Stop&nbsp;{stop?.stop_order}
              </Typography>
            )}
            label='Run'
            tooltip={`HopDrive's internal run & stop position`}
            style={{ minWidth: 104 }}
          />

          <Column
            name='customer'
            value={(stop: MissingRoStop) => `${getStopCustomerName(stop)} ${getStopCustomerPhone(stop)}`}
            renderer={(stop: MissingRoStop) => (
              <div>
                {getStopCustomerName(stop) && (
                  <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                    {getStopCustomerName(stop)}
                  </Typography>
                )}
                {getStopCustomerPhone(stop) && (
                  <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                    {formatPhone(getStopCustomerPhone(stop))}
                  </Typography>
                )}
              </div>
            )}
            label='Customer'
            style={{ minWidth: 144 }}
          />

          <Column
            name='scheduled'
            value={(stop: MissingRoStop) => getStopScheduledTime(stop)}
            renderer={(stop: MissingRoStop) => formatTime(getStopScheduledTime(stop)) || '-'}
            label='Scheduled'
            style={{ minWidth: 144 }}
          />

          <Column
            name='technician'
            value={(stop: MissingRoStop) => stop?.run?.driver?.user?.display_name}
            renderer={(stop: MissingRoStop) => stop?.run?.driver?.user?.display_name || '-'}
            label='Technician'
            style={{ minWidth: 144 }}
          />

          <Column
            name='status'
            value={(stop: MissingRoStop) => stop?.status}
            renderer={(stop: MissingRoStop) => formatStopStatus(stop?.status)}
            label='Status'
            style={{ minWidth: 120 }}
          />

          <Column
            name='vin'
            value={(stop: MissingRoStop) => getStopVin(stop)}
            renderer={(stop: MissingRoStop) => getStopVin(stop) || '-'}
            label='VIN'
            style={{ minWidth: 96 }}
          />

          <Column
            name='vehicle'
            value={(stop: MissingRoStop) => getStopVehicle(stop)}
            renderer={(stop: MissingRoStop) => getStopVehicle(stop) || '-'}
            label='Vehicle'
            style={{ minWidth: 192 }}
          />

          <Column
            name='address'
            value={(stop: MissingRoStop) => splitAddress(stop?.address) || '-'}
            label='Address'
            style={{ minWidth: 192 }}
          />
        </Table>
      ) : (
        <>
          <Spacer size={2} />
          <DefaultFallback
            fullscreen
            severity='warning'
            message='No mobile service stops missing an RO # found. Try adjusting your date range or search...'
          />
        </>
      )}
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  infoBox: css`
    padding: 16px;
    border: 1px solid ${theme?.palette?.divider};
    border-radius: 10px;
    background-color: ${theme?.palette?.action?.hover};
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default MissingRoServiceStopsSection;
